import React from "react"
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import CCORoofline from "../../fundamentals/CCORoofline";
import TeaserCardCallToAction, {CallToActionType} from "../C020TeaserCarousel/TeaserCardCallToAction";
import {useTheme} from "@mui/material/styles";
import CCOPicture from "../../fundamentals/CCOPicture";
import {CmTaxonomyType, SosDonationProductType} from "../../types";
import {DESCRIPTION_CHAR_LIMIT, ROOF_LINE_CHAR_LIMIT} from "../C020TeaserCarousel/TeaserCardContent";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import useWindowSize from "../../../utility/useWindowSize";
import CCOHeadlineAsText from "../../fundamentals/CCOHeadlineAsText";
import CCOAiGeneratedImageLabel, {
    AiGeneratedImageLabel,
    useAiGeneratedImageLabel
} from "../../fundamentals/CCOAiGeneratedImageLabel";


interface Props {
    item: SosDonationProductType,
    horizontal?: boolean,
    headlineType?: string,
    descriptionCharLimit?: number,
    callToActionCustomText?: string,
    suppressRoofline?: boolean,
    isPageView?: boolean,
    suppressCTA?: boolean
}

export const HOVER_TEXT_CLASS = 'product-card-hover-text';

const PRODUCT_BADGE_HEART_ICON = '/productBadgeHeart.svg';
const PRODUCT_BADGE_LINE_HEIGHT = 18;
const PRODUCT_BADGE_CAP_HEIGHT = 8.4;
const PRODUCT_BADGE_LEADING_TRIM = `-${(PRODUCT_BADGE_LINE_HEIGHT - PRODUCT_BADGE_CAP_HEIGHT) / 2}px`;

const PRODUCT_BADGE_STYLE = {
    alignItems: 'center',
    backgroundColor: '#D03000',
    boxSizing: 'border-box',
    color: 'common.white',
    display: 'inline-flex',
    gap: 2,
    height: '28px',
    justifyContent: 'center',
    position: 'absolute',
    px: 2,
    py: 1,
    right: 0,
    top: '16px',
    whiteSpace: 'nowrap',
    zIndex: 1,
} as const;

const PRODUCT_BADGE_TEXT_STYLE = {
    lineHeight: `${PRODUCT_BADGE_LINE_HEIGHT}px`,
    '&::before, &::after': {
        content: '""',
        display: 'table',
    },
    '&::before': {
        marginBottom: PRODUCT_BADGE_LEADING_TRIM,
    },
    '&::after': {
        marginTop: PRODUCT_BADGE_LEADING_TRIM,
    },
} as const;

const PRODUCT_BADGE_ICON_STYLE = {display: 'block', height: '20px', width: '20px'};

const hoverTextStyle = (color: string) => ({
    color: color,
    textDecoration: 'none',
    transition: 'color 1s ease-out',
});

export const productCardStyle = (props: Props, color: string) => (
    {
        display: 'grid',
        gridTemplateColumns: props.horizontal ? '1fr 1fr' : 'unset',
        gridTemplateRows: props.horizontal ? 'unset' : 'max-content max-content',
        height: '100%!important',
        width: '100%!important',
        '&:hover': props.item.teaserTarget ? {[`& .${HOVER_TEXT_CLASS}`]: hoverTextStyle(color)} : {},
    });

export function renderProductPicture(product: SosDonationProductType,
                                     t: TFunction,
                                     isHorizontal: boolean,
                                     isWindowExtraLarge: boolean,
                                     aiLabel?: AiGeneratedImageLabel) {
    function getBadgeText() {
        return product?.new ?
            t("c085_flag_new_product") :
            product?.recommendation ?
                t("c085_flag_recommendation_product") :
                undefined;
    }

    const badgeText = getBadgeText();
    const hasHeartIcon = !product?.new && !!product?.recommendation;
    const boxSx = {
        "& > div > img": {
            width: '100%!important',
            height: '100%'
        },
        alignSelf: 'start',
        display: 'inline-block',
        overflow: 'hidden',
        position: 'relative'
    };
    return product?.picture &&
        <Box sx={boxSx}>
            {isHorizontal && !(isWindowExtraLarge) ? renderHorizontalPicture(product) :
                renderNormalPicture(product)}
            {badgeText && renderBadge(badgeText, hasHeartIcon)}
            {aiLabel && <CCOAiGeneratedImageLabel label={aiLabel} variant='productCard'/>}
        </Box>;
}

function renderBadge(text: string, hasHeartIcon: boolean) {
    return <Box component='span' data-cypress='productBadge' sx={PRODUCT_BADGE_STYLE}>
        {hasHeartIcon && <Box alt=''
                              aria-hidden='true'
                              component='img'
                              src={PRODUCT_BADGE_HEART_ICON}
                              sx={PRODUCT_BADGE_ICON_STYLE}/>}
        <Typography component='span' sx={PRODUCT_BADGE_TEXT_STYLE} variant='overline'>
            {text}
        </Typography>
    </Box>;
}

function formatPrice(price: string, t: TFunction) {
    return price ? price + ' ' + t('cc0_euro') : '';
}

export function renderPrice(price: string | null | undefined, t: TFunction, priceStyle: any) {
    return price ? <CCOHeadlineAsText textAlign='left'
                                      className={HOVER_TEXT_CLASS}
                                      title={formatPrice(price, t)}
                                      variant='h3'
                                      sx={priceStyle}/> : null;
}

function renderNormalPicture(product: SosDonationProductType) {
    return <CCOPicture name={product?.picture?.name}
                       picture={product?.picture}
                       orientation='landscape_ratio16x9' width='585'
                       height='329'/>
}

function renderHorizontalPicture(product: SosDonationProductType) {
    return <CCOPicture name={product?.picture?.name}
                       picture={product?.picture}
                       orientation='landscape_ratio4x3' width='768'
                       height='576'/>
}

function renderContent(props: Props, t: TFunction, isWindowExtraLarge: boolean, priceStyle: any,
                       aiLabel?: AiGeneratedImageLabel) {
    return <React.Fragment>
        {
            renderPicture(props, t, isWindowExtraLarge, aiLabel)
        }
        {
            renderText(props, t, priceStyle)
        }
    </React.Fragment>
}

function renderPicture(props: Props, t: TFunction, isWindowExtraLarge: boolean, aiLabel?: AiGeneratedImageLabel) {
    return renderProductPicture(props.item, t, !!props.horizontal, isWindowExtraLarge, aiLabel);
}

function renderText(props: Props, t: TFunction, priceStyle: any) {
    const CARD_CONTENT_STYLE = {
        'div:last-child': {
            marginBottom: '0 !important',
        },
        'span > div:last-child': {
            marginBottom: '24px !important',
        },
        paddingLeft: '24px',
        paddingTop: '24px',
        height: '100%!important',
        width: '100%!important',
        backgroundColor: 'inherit',
    }

    return <CardContent sx={CARD_CONTENT_STYLE}>
        {renderTitle(props)}
        {renderRichText(props)}
        {renderPrice(props.item?.price, t, priceStyle)}
        {props.isPageView || props.suppressCTA ? null : renderCTA(props, t)}
    </CardContent>
}

function renderCTA(props: Props, t: TFunction) {
    return <TeaserCardCallToAction item={props.item}
                                   callToActionType={CallToActionType.BUTTON}
                                   callToActionCustomText={t('c085_product_cta') || undefined}
                                   insideCardWithLink/>
}

function getCategoryTitle(item: SosDonationProductType): string {
    if (item.category && item.category.length > 0 && (item.category[0] as CmTaxonomyType).value) {
        const tax = item.category[0] as CmTaxonomyType;
        return tax.value!;
    } else {
        return item.title!;
    }
}

function renderTitle(props: Props) {
    return <React.Fragment>
        {props.suppressRoofline ? null : renderRoofline(props)}
        <CCOHeadlineAsText className={HOVER_TEXT_CLASS}
                           textAlign='left'
                           title={props.item.teaserTitle}
                           metadataId={props.item.id}
                           propertyName='properties.teaserTitle'
                           variant='h6'
                           sx={{marginBottom: '0.35em'}}/>
    </React.Fragment>;
}

function renderRoofline(props: Props) {
    const subline = getCategoryTitle(props.item);
    return <CCORoofline metadataId={props.item.id}
                        roofLine={subline}
                        roofLineCharLimit={ROOF_LINE_CHAR_LIMIT}
                        propertyName='properties.title'/>
}

function renderRichText(props: Props) {
    const propName = props.isPageView ? 'properties.detailText' : 'properties.teaserText';
    return <CCORichText metadataId={props.item.id}
                        textAsTree={props.suppressRoofline ? props.item.detailText?.textAsTree :
                            props.item.teaserText?.textAsTree}
                        propertyName={propName}
                        charLimit={props.descriptionCharLimit ? props.descriptionCharLimit :
                            DESCRIPTION_CHAR_LIMIT}/>;
}

/**
 * TeaserCardContent für C085ProductTeaser
 */
function ProductTeaserCardContent(props: Props) {
    const theme = useTheme();
    const color = theme.palette.primary.main;
    const {isWindowExtraLarge, lowerThanWindowMedium, lowerThanWindowLarge} = useWindowSize();
    const {t} = useTranslation();
    const aiLabel = useAiGeneratedImageLabel(props.item?.picture);

    // Extrawurst für den Preis. Bei kleineren Brakpoints muss die Font-Size auch kleiner werden
    function getPriceStyle() {
        const normalStyle = {marginBottom: '0.35em'};
        const lowerThanMedStyle = {marginBottom: '0.35em', fontSize: '1.5rem!important'};
        const lowerThanLStyle = {marginBottom: '0.35em', fontSize: '1.75rem!important'};
        return lowerThanWindowMedium ? lowerThanMedStyle :
            lowerThanWindowLarge ? lowerThanLStyle : normalStyle;
    }

    return <Box id={props.item.id} sx={productCardStyle(props, color)}>
        {
            renderContent(props, t, isWindowExtraLarge, getPriceStyle(), aiLabel)
        }
    </Box>
}


export default ProductTeaserCardContent
